import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {Box, Divider, Typography} from '@mui/material';
import {t} from 'i18next';
import ArrAccordion from '../../../../common/arr-accordion/ArrAccordion';
import ArrTransferList, {
  KeyValue,
} from '../../../../common/arr-transfer-list/ArrTransferList';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import './MetadataFields.scss';
import {ReactComponent as ArrowBackward} from '../../../../../assets/images/back-arrow.svg';
import {ReactComponent as ArrowForward} from '../../../../../assets/images/arrow-forward.svg';
import {ReactComponent as DoubleArrowBackward} from '../../../../../assets/images/double-arrow-backward.svg';
import {ReactComponent as DoubleArrowForward} from '../../../../../assets/images/double-arrow-forward.svg';
import {ReactComponent as RowChecked} from '../../../../../assets/images/row-checked.svg';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {
  setFields,
  setSelectedMetadataFieldNames,
  IRequestField,
} from '../../../../../redux/slice/createCustomIndexSlice';
import {useCustomIndexFields} from '../../../../../hooks/ingestions/useIngestions';
import {
  INITIAL_RENDER_COUNT,
  RENDER_INCREMENT,
  SCROLL_THRESHOLD,
} from '../../../../../shared/constants';

type FieldValue = number | string;

type MetadataField = KeyValue & {
  analyzerId: number;
  analyzerKey: string;
  analyzerName?: string;
  isDefault: boolean;
  isEditable?: boolean;
  fieldType?: string;
  comparableFieldId?: FieldValue;
};

type CustomIndexFieldResponseItem = {
  FieldId?: FieldValue;
  FieldID?: FieldValue;
  FieldName?: string;
  ParentFieldId?: FieldValue | null;
  ParentFieldID?: FieldValue | null;
  AnalyzerId?: FieldValue;
  AnalyzerID?: FieldValue;
  AnalyzerKey?: string;
  AnalyzerName?: string;
  FieldType?: string;
  IsDefault?: boolean;
  IsEditable?: boolean;
  fieldId?: FieldValue;
  fieldID?: FieldValue;
  fieldName?: string;
  fieldType?: string;
  parentFieldId?: FieldValue | null;
  parentFieldID?: FieldValue | null;
  analyzerId?: FieldValue;
  analyzerID?: FieldValue;
  analyzerKey?: string;
  analyzerName?: string;
  isDefault?: boolean;
  isEditable?: boolean;
};

type SavedRequestField = IRequestField & {
  FieldId?: FieldValue;
  FieldID?: FieldValue;
  fieldId?: FieldValue;
  fieldID?: FieldValue;
  FieldName?: string;
  fieldName?: string;
  AnalyzerId?: FieldValue;
  AnalyzerID?: FieldValue;
  analyzerId?: FieldValue;
  analyzerID?: FieldValue;
  AnalyzerKey?: string;
  analyzerKey?: string;
  AnalyzerName?: string;
  analyzerName?: string;
  FieldType?: string;
  fieldType?: string;
  IsDefault?: boolean;
  isDefault?: boolean;
};

const normalizeFieldId = (fieldId?: FieldValue | null) =>
  fieldId === undefined || fieldId === null ? '' : String(fieldId).trim();

const getCustomFieldId = (field: CustomIndexFieldResponseItem) =>
  field.FieldId ?? field.FieldID ?? field.fieldId ?? field.fieldID;

const getCustomParentFieldId = (field: CustomIndexFieldResponseItem) =>
  field.ParentFieldId ??
  field.ParentFieldID ??
  field.parentFieldId ??
  field.parentFieldID;

const getComparableCustomFieldId = (field: CustomIndexFieldResponseItem) => {
  const parentFieldId = getCustomParentFieldId(field);
  return parentFieldId === undefined || parentFieldId === null
    ? getCustomFieldId(field)
    : parentFieldId;
};

const getSavedFieldId = (field: SavedRequestField) =>
  field.fieldId ?? field.fieldID ?? field.FieldId ?? field.FieldID;

const getCustomAnalyzerId = (field: CustomIndexFieldResponseItem) =>
  field.AnalyzerId ?? field.AnalyzerID ?? field.analyzerId ?? field.analyzerID;

const getSavedAnalyzerId = (field: SavedRequestField) =>
  field.analyzerId ?? field.analyzerID ?? field.AnalyzerId ?? field.AnalyzerID;

const MetadataFields: React.FC = () => {
  const dispatch = useDispatch();
  const {isReadOnlyView, createCustomIndex} = useSelector(
    (state: RootState) => state.indexManagement
  );
  const savedFields: IRequestField[] = createCustomIndex?.fields ?? [];
  const {data: customIndexFields} = useCustomIndexFields();

  const customIndexFieldItems = useMemo(
    () =>
      Array.isArray(customIndexFields)
        ? (customIndexFields as CustomIndexFieldResponseItem[])
        : [],
    [customIndexFields]
  );

  const metadataFields: MetadataField[] = useMemo(() => {
    const uniqueFieldsMap = new Map<string, MetadataField>();
    customIndexFieldItems.forEach((field) => {
      const fieldId = getCustomFieldId(field);
      const comparableFieldId = getComparableCustomFieldId(field);
      const fieldName = field.FieldName ?? field.fieldName;
      const analyzerId = getCustomAnalyzerId(field);
      const analyzerKey = field.AnalyzerKey ?? field.analyzerKey;
      const analyzerName = field.AnalyzerName ?? field.analyzerName;
      const fieldType = field.FieldType ?? field.fieldType;
      const isDefault = field.IsDefault ?? field.isDefault;
      const isEditable = field.IsEditable ?? field.isEditable;
      const normalizedFieldId = normalizeFieldId(fieldId);
      const normalizedComparableFieldId = normalizeFieldId(comparableFieldId);
      const parsedFieldId = Number(fieldId);
      const parsedAnalyzerId = Number(analyzerId);
      if (
        normalizedFieldId === '' ||
        normalizedComparableFieldId === '' ||
        analyzerId === undefined ||
        analyzerId === null ||
        Number.isNaN(parsedFieldId) ||
        Number.isNaN(parsedAnalyzerId)
      ) {
        return;
      }
      if (!uniqueFieldsMap.has(normalizedFieldId)) {
        uniqueFieldsMap.set(normalizedFieldId, {
          id: parsedFieldId,
          label: fieldName ?? '',
          analyzerId: parsedAnalyzerId,
          analyzerKey: analyzerKey ?? '',
          analyzerName,
          isDefault: isDefault ?? false,
          isEditable,
          fieldType,
          comparableFieldId,
        });
      }
    });
    return Array.from(uniqueFieldsMap.values());
  }, [customIndexFieldItems]);

  const savedFieldIds = useMemo(() => {
    return new Set(
      savedFields
        .map((field) =>
          normalizeFieldId(getSavedFieldId(field as SavedRequestField))
        )
        .filter((fieldId) => fieldId !== '')
    );
  }, [savedFields]);

  const customFieldsByComparableId = useMemo(() => {
    const fieldsMap = new Map<string, CustomIndexFieldResponseItem>();
    customIndexFieldItems.forEach((field) => {
      const normalizedComparableFieldId = normalizeFieldId(
        getComparableCustomFieldId(field)
      );
      if (
        normalizedComparableFieldId !== '' &&
        !fieldsMap.has(normalizedComparableFieldId)
      ) {
        fieldsMap.set(normalizedComparableFieldId, field);
      }
    });
    return fieldsMap;
  }, [customIndexFieldItems]);

  const savedSelected: MetadataField[] = useMemo(
    () =>
      metadataFields.filter((field) =>
        savedFieldIds.has(
          normalizeFieldId((field.comparableFieldId ?? field.id) as FieldValue)
        )
      ),
    [metadataFields, savedFieldIds]
  );

  const readOnlySelected: MetadataField[] = useMemo(() => {
    return savedFields.reduce<MetadataField[]>((acc, savedField) => {
      const savedRequestField = savedField as SavedRequestField;
      const normalizedSavedFieldId = normalizeFieldId(
        getSavedFieldId(savedRequestField)
      );
      if (normalizedSavedFieldId === '') {
        return acc;
      }
      const matchingCustomField = customFieldsByComparableId.get(
        normalizedSavedFieldId
      );
      if (!matchingCustomField) {
        return acc;
      }
      const customAnalyzerId = getCustomAnalyzerId(matchingCustomField);
      const savedAnalyzerId = getSavedAnalyzerId(savedRequestField);
      const analyzerId = customAnalyzerId ?? savedAnalyzerId ?? 0;
      const customFieldId = getCustomFieldId(matchingCustomField);
      const customComparableFieldId =
        getComparableCustomFieldId(matchingCustomField);
      const savedFieldId = getSavedFieldId(savedRequestField);
      const parsedCustomFieldId = Number(customFieldId);
      const parsedSavedFieldId = Number(savedFieldId);
      const parsedAnalyzerId = Number(analyzerId);
      acc.push({
        id: Number.isNaN(parsedSavedFieldId)
          ? Number.isNaN(parsedCustomFieldId)
            ? normalizedSavedFieldId
            : parsedCustomFieldId
          : parsedSavedFieldId,
        label:
          matchingCustomField.FieldName ?? matchingCustomField.fieldName ?? '',
        analyzerId: Number.isNaN(parsedAnalyzerId) ? 0 : parsedAnalyzerId,
        analyzerKey:
          matchingCustomField.AnalyzerKey ??
          matchingCustomField.analyzerKey ??
          savedRequestField.AnalyzerKey ??
          savedRequestField.analyzerKey ??
          '',
        analyzerName:
          matchingCustomField.AnalyzerName ??
          matchingCustomField.analyzerName ??
          savedRequestField.AnalyzerName ??
          savedRequestField.analyzerName,
        fieldType:
          matchingCustomField.FieldType ??
          matchingCustomField.fieldType ??
          savedRequestField.FieldType ??
          savedRequestField.fieldType,
        isDefault:
          matchingCustomField.IsDefault ??
          matchingCustomField.isDefault ??
          savedRequestField.IsDefault ??
          savedRequestField.isDefault ??
          false,
        isEditable:
          matchingCustomField.IsEditable ?? matchingCustomField.isEditable,
        comparableFieldId: customComparableFieldId,
      });
      return acc;
    }, []);
  }, [customFieldsByComparableId, savedFields]);

  const selectedFields = useMemo(
    () => (isReadOnlyView ? readOnlySelected : savedSelected),
    [isReadOnlyView, readOnlySelected, savedSelected]
  );

  const savedAvailable: MetadataField[] = useMemo(
    () =>
      metadataFields.filter(
        (field) =>
          !savedFieldIds.has(
            normalizeFieldId(
              (field.comparableFieldId ?? field.id) as FieldValue
            )
          )
      ),
    [metadataFields, savedFieldIds]
  );

  const [rightList, setRightList] = useState<MetadataField[]>(selectedFields);
  const [leftList, setLeftList] = useState<MetadataField[]>(savedAvailable);
  const [leftSelected, setLeftSelected] = useState<MetadataField[]>([]);
  const [rightSelected, setRightSelected] = useState<MetadataField[]>([]);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [leftRenderCount, setLeftRenderCount] = useState(INITIAL_RENDER_COUNT);
  const [rightRenderCount, setRightRenderCount] =
    useState(INITIAL_RENDER_COUNT);

  useEffect(() => {
    setRightList(selectedFields);
    setLeftList(savedAvailable);
  }, [selectedFields, savedAvailable]);

  useEffect(() => {
    const updatedSelectedMetadataFieldNames = selectedFields.map((item) => ({
      fieldId: Number(item.id),
      fieldName: item.label ?? '',
      analyzerKey: item.analyzerKey,
      ...(item.analyzerName !== undefined
        ? {analyzerName: item.analyzerName}
        : {}),
      isDefault: item.isDefault,
      ...(item.fieldType !== undefined ? {fieldType: item.fieldType} : {}),
    }));
    dispatch(setSelectedMetadataFieldNames(updatedSelectedMetadataFieldNames));
  }, [dispatch, selectedFields]);

  const filteredLeftList = useMemo(
    () =>
      leftList.filter((i) =>
        (i.label ?? '').toLowerCase().includes(leftSearch.toLowerCase())
      ),
    [leftList, leftSearch]
  );

  const filteredRightList = useMemo(
    () =>
      rightList.filter((i) =>
        (i.label ?? '').toLowerCase().includes(rightSearch.toLowerCase())
      ),
    [rightList, rightSearch]
  );

  const renderedLeftList = useMemo(
    () => filteredLeftList.slice(0, leftRenderCount),
    [filteredLeftList, leftRenderCount]
  );

  const renderedRightList = useMemo(
    () => filteredRightList.slice(0, rightRenderCount),
    [filteredRightList, rightRenderCount]
  );

  useEffect(() => {
    setLeftRenderCount(INITIAL_RENDER_COUNT);
  }, [leftSearch, leftList]);

  useEffect(() => {
    setRightRenderCount(INITIAL_RENDER_COUNT);
  }, [rightSearch, rightList]);

  const handleLazyLoadScroll = useCallback(
    (
      e: React.UIEvent<HTMLElement>,
      filteredListLength: number,
      renderCount: number,
      setRenderCount: React.Dispatch<React.SetStateAction<number>>
    ) => {
      const scrollElement = e.target as HTMLElement;
      const {scrollTop, scrollHeight, clientHeight} = scrollElement;
      const hasMoreItems = renderCount < filteredListLength;
      const isScrollable = scrollHeight > clientHeight;
      const isNearBottom =
        scrollHeight - scrollTop - clientHeight <= SCROLL_THRESHOLD;

      if (hasMoreItems && isScrollable && isNearBottom) {
        setRenderCount((prev) =>
          Math.min(prev + RENDER_INCREMENT, filteredListLength)
        );
      }
    },
    []
  );

  const syncToStore = (items: MetadataField[]) => {
    const updatedFields: IRequestField[] = items.map((item) => ({
      fieldId: Number(item.id),
      analyzerId: item.analyzerId,
    }));
    const updatedSelectedMetadataFieldNames = items.map((item) => ({
      fieldId: Number(item.id),
      fieldName: item.label ?? '',
      analyzerKey: item.analyzerKey,
      ...(item.analyzerName !== undefined
        ? {analyzerName: item.analyzerName}
        : {}),
      isDefault: item.isDefault,
      ...(item.fieldType !== undefined ? {fieldType: item.fieldType} : {}),
    }));
    dispatch(setFields(updatedFields));
    dispatch(setSelectedMetadataFieldNames(updatedSelectedMetadataFieldNames));
  };

  const handleLeftSelection = (_e: any, field: KeyValue) => {
    if (isReadOnlyView) return;
    const metadataField = field as MetadataField;
    setLeftSelected((prev) =>
      prev.some((i) => i.id === metadataField.id)
        ? prev.filter((i) => i.id !== metadataField.id)
        : [...prev, metadataField]
    );
  };

  const handleRightSelection = (_e: any, field: KeyValue) => {
    if (isReadOnlyView) return;
    const metadataField = field as MetadataField;
    setRightSelected((prev) =>
      prev.some((i) => i.id === metadataField.id)
        ? prev.filter((i) => i.id !== metadataField.id)
        : [...prev, metadataField]
    );
  };

  const moveSelectedToRight = () => {
    if (isReadOnlyView || leftSelected.length === 0) return;
    const updatedRight = [...rightList, ...leftSelected];
    setRightList(updatedRight);
    setLeftList((prev) =>
      prev.filter((i) => !leftSelected.some((s) => s.id === i.id))
    );
    setLeftSelected([]);
    syncToStore(updatedRight);
  };

  const moveSelectedToLeft = () => {
    if (isReadOnlyView || rightSelected.length === 0) return;
    const updatedRight = rightList.filter(
      (i) => !rightSelected.some((s) => s.id === i.id)
    );
    setRightList(updatedRight);
    setLeftList((prev) => [...prev, ...rightSelected]);
    setRightSelected([]);
    syncToStore(updatedRight);
  };

  const moveMultipleToRight = () => {
    if (isReadOnlyView || filteredLeftList.length === 0) return;
    const movedIds = new Set(filteredLeftList.map((item) => item.id));
    const updatedRight = [...rightList, ...filteredLeftList];
    setRightList(updatedRight);
    setLeftList((prev) => prev.filter((item) => !movedIds.has(item.id)));
    setLeftSelected([]);
    syncToStore(updatedRight);
  };

  const moveMultipleToLeft = () => {
    if (isReadOnlyView || filteredRightList.length === 0) return;
    const movedIds = new Set(filteredRightList.map((item) => item.id));
    const updatedRight = rightList.filter((item) => !movedIds.has(item.id));
    setRightList(updatedRight);
    setLeftList((prev) => [...prev, ...filteredRightList]);
    setRightSelected([]);
    syncToStore(updatedRight);
  };

  const handleTransferButtonKeyDown = (e: React.KeyboardEvent) => {
    const buttons = Array.from(
      document.querySelectorAll('.tranfer-action-icon-button')
    ) as HTMLElement[];
    const index = buttons.indexOf(e.currentTarget as HTMLElement);
    if (e.key === 'ArrowDown' && index < buttons.length - 1) {
      e.preventDefault();
      buttons[index + 1].focus();
    }
    if (e.key === 'ArrowUp' && index > 0) {
      e.preventDefault();
      buttons[index - 1].focus();
    }
  };

  return (
    <Box className="metadata-container-create-custom-index">
      <ArrAccordion
        sections={[
          {
            id: 'metaData',
            title: (
              <Typography
                className="metadata-title"
                data-testid={`metadata-fields-metadata-fields`}
              >
                {t('indexManagement.customIndex.stepper.metadataFields')}
              </Typography>
            ),
            content: (
              <Box className="metadate-content">
                <Box className="metadata-heading">
                  <Box className="metadata-content-heading">
                    <Typography
                      className="metadata-content-heading-text"
                      data-testid={`metadata-fields-header-text`}
                      tabIndex={0}
                    >
                      {t(
                        'indexManagement.customIndex.metadataFields.headerText'
                      )}
                    </Typography>
                  </Box>
                  <Box className="metadata-content-description">
                    <Typography
                      className="metadata-content-description-text"
                      data-testid={`metadata-fields-description`}
                      tabIndex={0}
                    >
                      {t(
                        'indexManagement.customIndex.metadataFields.description'
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  className={`fields-transfer-wrapper ${
                    isReadOnlyView ? 'readonly-fields-transfer-wrapper' : ''
                  }`}
                >
                  <Box
                    role="region"
                    aria-label="Available Fields"
                    className="transfer-list-box"
                    onScrollCapture={(e) =>
                      handleLazyLoadScroll(
                        e,
                        filteredLeftList.length,
                        leftRenderCount,
                        setLeftRenderCount
                      )
                    }
                  >
                    <ArrTransferList
                      title={t(
                        'indexManagement.customIndex.metadataFields.availableFields'
                      )}
                      displayedList={renderedLeftList}
                      selectedList={leftSelected}
                      checkboxDataTestId="metadata-fields-left-transfer-list-checkbox"
                      fullWidthSearchField
                      clickOnRow={!isReadOnlyView}
                      onChange={handleLeftSelection}
                      dimSelect={true}
                      showCheckbox={false}
                      hideItemDivider={true}
                      endIcon={(item) =>
                        leftSelected.some((s) => s.id === item.id) ? (
                          <RowChecked />
                        ) : null
                      }
                      endIconPosition="right"
                      showSearchField={true}
                      disableItemTabFocus={true}
                      searchProps={{
                        value: leftSearch,
                        onChange: (e) => setLeftSearch(e.target.value),
                        placeholder: t('verticalNavBar.Search'),
                        type: 'search',
                        inputProps: {'aria-label': 'Search available fields'},
                        dataTestId: 'metadat-fields-transfer-list-left',
                      }}
                    />
                  </Box>
                  <Box className="transfer-action-buttons">
                    <Box className="transfer-buttons-blocks">
                      <ArrIconButton
                        className="tranfer-action-icon-button arrow-double"
                        onClick={moveMultipleToRight}
                        onKeyDown={handleTransferButtonKeyDown}
                        disabled={
                          filteredLeftList.length === 0 || isReadOnlyView
                        }
                        icon={<DoubleArrowBackward />}
                        ariaLabel="Add Multiple"
                        tooltipContent={t(
                          'indexManagement.customIndex.metadataFields.addSelectedFields'
                        )}
                        disableTooltipTabFocus={true}
                        testId="metadata-fields-add-multiple-icon-button"
                      />
                      <ArrIconButton
                        className="tranfer-action-icon-button tranfer-action-forward arrow-single"
                        onClick={moveSelectedToRight}
                        onKeyDown={handleTransferButtonKeyDown}
                        disabled={leftSelected.length === 0 || isReadOnlyView}
                        icon={<ArrowForward />}
                        ariaLabel="Add Selected"
                        tooltipContent={t(
                          'indexManagement.customIndex.metadataFields.addSelectedField'
                        )}
                        disableTooltipTabFocus={true}
                        testId="metadata-fields-add-selected-icon-button"
                      />
                    </Box>
                    <Divider className="move-buton-divider" />
                    <Box className="transfer-buttons-blocks">
                      <ArrIconButton
                        className="tranfer-action-icon-button arrow-single"
                        onClick={moveSelectedToLeft}
                        onKeyDown={handleTransferButtonKeyDown}
                        disabled={rightSelected.length === 0 || isReadOnlyView}
                        icon={<ArrowBackward />}
                        ariaLabel="Remove Selected"
                        tooltipContent={t(
                          'indexManagement.customIndex.metadataFields.removeSelectedField'
                        )}
                        disableTooltipTabFocus={true}
                        testId="metadata-fields-remove-selected-icon-button"
                      />
                      <ArrIconButton
                        className="tranfer-action-icon-button tranfer-action-forward arrow-double"
                        onClick={moveMultipleToLeft}
                        onKeyDown={handleTransferButtonKeyDown}
                        disabled={
                          filteredRightList.length === 0 || isReadOnlyView
                        }
                        icon={<DoubleArrowForward />}
                        ariaLabel="Remove Multiple"
                        tooltipContent={t(
                          'indexManagement.customIndex.metadataFields.removeSelectedFields'
                        )}
                        disableTooltipTabFocus={true}
                        testId="metadata-fields-remove-multiple-icon-button"
                      />
                    </Box>
                  </Box>
                  <Box
                    role="region"
                    aria-label="Selected Fields"
                    className="transfer-list-box"
                    onScrollCapture={(e) =>
                      handleLazyLoadScroll(
                        e,
                        filteredRightList.length,
                        rightRenderCount,
                        setRightRenderCount
                      )
                    }
                  >
                    <ArrTransferList
                      title={t(
                        'indexManagement.customIndex.metadataFields.selectedFields'
                      )}
                      displayedList={renderedRightList}
                      selectedList={rightSelected}
                      checkboxDataTestId="metadata-fields-right-transfer-list-checkbox"
                      clickOnRow={!isReadOnlyView}
                      onChange={handleRightSelection}
                      fullWidthSearchField
                      showCheckbox={false}
                      dimSelect={true}
                      hideItemDivider={true}
                      endIcon={(item) =>
                        rightSelected.some((s) => s.id === item.id) ? (
                          <RowChecked />
                        ) : null
                      }
                      endIconPosition="right"
                      showSearchField={true}
                      disableItemTabFocus={true}
                      searchProps={{
                        value: rightSearch,
                        onChange: (e) => setRightSearch(e.target.value),
                        placeholder: t('verticalNavBar.Search'),
                        type: 'search',
                        inputProps: {'aria-label': 'Search selected fields'},
                        dataTestId: 'metadat-fields-transfer-list-right',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ),
          },
        ]}
        expandedSection="metaData"
        showExpandIcon={false}
        className="metadata-accordion"
        dataTestId="metadata-fields"
      />
    </Box>
  );
};

export default MetadataFields;
